import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import Button from '../ui/Button';
import { blogService, BlogPost, BlogConnection } from '../../services/blogService';
import { ChevronLeft, ChevronRight, Calendar as CalIcon, FileText, Loader2 } from 'lucide-react';
import BlogPostEditor from './BlogPostEditor';

// A post appears on the calendar at its scheduled_at (scheduled) or published_at (published).
// Drafts without a schedule are skipped.
function postDate(p: BlogPost): Date | null {
  if (p.status === 'scheduled' && p.scheduled_at) return new Date(p.scheduled_at);
  if (p.status === 'published' && p.published_at) return new Date(p.published_at);
  return null;
}

function sameDay(a: Date, b: Date): boolean {
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
}

function dateKey(d: Date): string {
  return `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
}

const BlogCalendar: React.FC = () => {
  const { t } = useTranslation();
  const [posts, setPosts] = useState<BlogPost[]>([]);
  const [loading, setLoading] = useState(true);
  const [cursor, setCursor] = useState(() => {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), 1);
  });
  const [connections, setConnections] = useState<BlogConnection[]>([]);
  const [editingPost, setEditingPost] = useState<BlogPost | null>(null);
  const [editorOpen, setEditorOpen] = useState(false);

  const reloadPosts = useCallback(async () => {
    setLoading(true);
    try {
      const res = await blogService.listPosts({ limit: 200 });
      setPosts(res?.data ?? []);
    } catch (err: any) {
      toast.error(
        err?.response?.data?.message ?? err?.message ?? t('blogSeo.calendar.loadError', 'Erro ao carregar posts')
      );
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    reloadPosts();
  }, [reloadPosts]);

  useEffect(() => {
    blogService
      .listConnections()
      .then((r) => setConnections(r?.data ?? []))
      .catch(() => {
        /* non-critical */
      });
  }, []);

  // Open the shared editor for a calendar post. Fetch the full post (the calendar
  // list may omit content); fall back to the list item if the fetch fails.
  const openEditor = useCallback(async (post: BlogPost) => {
    try {
      const res = await blogService.getPost(post.id);
      setEditingPost(res?.data ?? post);
    } catch {
      setEditingPost(post);
    }
    setEditorOpen(true);
  }, []);

  const today = useMemo(() => new Date(), []);

  // Map of dateKey -> posts on that day (only posts with a valid calendar date).
  const byDay = useMemo(() => {
    const map = new Map<string, { post: BlogPost; date: Date }[]>();
    for (const p of posts) {
      const d = postDate(p);
      if (!d) continue;
      const key = dateKey(d);
      const arr = map.get(key) ?? [];
      arr.push({ post: p, date: d });
      map.set(key, arr);
    }
    // sort each day's posts by time
    for (const arr of map.values()) arr.sort((a, b) => a.date.getTime() - b.date.getTime());
    return map;
  }, [posts]);

  // Build a 6-week (42 cell) grid starting from the Sunday on/ before the first of month.
  const cells = useMemo(() => {
    const firstOfMonth = new Date(cursor.getFullYear(), cursor.getMonth(), 1);
    const start = new Date(firstOfMonth);
    start.setDate(start.getDate() - start.getDay()); // back up to Sunday
    const out: Date[] = [];
    for (let i = 0; i < 42; i++) {
      const d = new Date(start);
      d.setDate(start.getDate() + i);
      out.push(d);
    }
    return out;
  }, [cursor]);

  // Whether the current month has any posts (used for the empty state).
  const monthHasPosts = useMemo(
    () => cells.some((d) => d.getMonth() === cursor.getMonth() && byDay.has(dateKey(d))),
    [cells, cursor, byDay]
  );

  const monthLabel = cursor.toLocaleDateString('pt-BR', { month: 'long', year: 'numeric' });
  const monthLabelCap = monthLabel.charAt(0).toUpperCase() + monthLabel.slice(1);

  const weekdays = useMemo(() => {
    // Sunday-first short weekday names in pt-BR: Dom, Seg, ...
    const base = new Date(2024, 0, 7); // a Sunday
    return Array.from({ length: 7 }, (_, i) => {
      const d = new Date(base);
      d.setDate(base.getDate() + i);
      const name = d.toLocaleDateString('pt-BR', { weekday: 'short' }).replace('.', '');
      return name.charAt(0).toUpperCase() + name.slice(1);
    });
  }, []);

  const goPrev = () => setCursor((c) => new Date(c.getFullYear(), c.getMonth() - 1, 1));
  const goNext = () => setCursor((c) => new Date(c.getFullYear(), c.getMonth() + 1, 1));
  const goToday = () => {
    const now = new Date();
    setCursor(new Date(now.getFullYear(), now.getMonth(), 1));
  };

  const fmtTime = (d: Date) => d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-2">
          <CalIcon className="w-5 h-5 text-primary-600 dark:text-primary-400" />
          <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 capitalize">{monthLabelCap}</h2>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="secondary" size="sm" leftIcon={<CalIcon className="w-4 h-4" />} onClick={goToday}>
            {t('blogSeo.calendar.today', 'Hoje')}
          </Button>
          <Button variant="ghost" size="sm" onClick={goPrev} aria-label={t('blogSeo.calendar.prevMonth', 'Mês anterior')}>
            <ChevronLeft className="w-4 h-4" />
          </Button>
          <Button variant="ghost" size="sm" onClick={goNext} aria-label={t('blogSeo.calendar.nextMonth', 'Próximo mês')}>
            <ChevronRight className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* Legend */}
      <div className="flex flex-wrap items-center gap-4 text-xs text-gray-600 dark:text-gray-400">
        <span className="inline-flex items-center gap-1.5">
          <span className="w-2.5 h-2.5 rounded-full bg-amber-500" />
          {t('blogSeo.calendar.scheduled', 'Agendado')}
        </span>
        <span className="inline-flex items-center gap-1.5">
          <span className="w-2.5 h-2.5 rounded-full bg-green-500" />
          {t('blogSeo.calendar.published', 'Publicado')}
        </span>
      </div>

      {loading ? (
        <div className="flex flex-col items-center justify-center gap-3 py-24 text-gray-500 dark:text-gray-400">
          <Loader2 className="w-8 h-8 animate-spin text-primary-500" />
          <span className="text-sm">{t('blogSeo.calendar.loading', 'Carregando...')}</span>
        </div>
      ) : (
        <div className="rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden bg-white dark:bg-gray-900/40">
          {/* Weekday header */}
          <div className="grid grid-cols-7 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/60">
            {weekdays.map((w) => (
              <div
                key={w}
                className="px-2 py-2 text-center text-[11px] font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400"
              >
                {w}
              </div>
            ))}
          </div>

          {/* Day cells */}
          <div className="grid grid-cols-7">
            {cells.map((d, idx) => {
              const inMonth = d.getMonth() === cursor.getMonth();
              const isToday = sameDay(d, today);
              const dayPosts = byDay.get(dateKey(d)) ?? [];
              const shown = dayPosts.slice(0, 3);
              const extra = dayPosts.length - shown.length;
              return (
                <div
                  key={idx}
                  className={[
                    'relative min-h-[96px] border-b border-r border-gray-200 dark:border-gray-700 p-1.5 transition-colors',
                    'hover:bg-gray-50 dark:hover:bg-gray-800/60',
                    inMonth ? 'bg-white dark:bg-gray-800/40' : 'bg-gray-50/60 dark:bg-gray-900/30',
                    isToday ? 'ring-2 ring-inset ring-primary-500' : '',
                  ].join(' ')}
                >
                  <div
                    className={[
                      'mb-1 flex items-center justify-center text-xs font-medium',
                      'h-5 w-5 rounded-full',
                      isToday ? 'bg-primary-600 text-white' : inMonth ? 'text-gray-700 dark:text-gray-300' : 'text-gray-400 dark:text-gray-600',
                    ].join(' ')}
                  >
                    {d.getDate()}
                  </div>
                  <div className="space-y-1">
                    {shown.map(({ post, date }) => (
                      <button
                        key={post.id}
                        type="button"
                        onClick={() => openEditor(post)}
                        title={post.title}
                        className="flex w-full items-center gap-1 rounded-md border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 px-1.5 py-1 text-left text-[11px] hover:border-primary-400 hover:bg-primary-50 dark:hover:bg-gray-700 transition-colors"
                      >
                        <span
                          className={[
                            'flex-shrink-0 w-2 h-2 rounded-full',
                            post.status === 'scheduled' ? 'bg-amber-500' : 'bg-green-500',
                          ].join(' ')}
                        />
                        <span className="flex-1 truncate text-gray-800 dark:text-gray-200">{post.title}</span>
                        <span className="flex-shrink-0 text-[10px] text-gray-400 dark:text-gray-500">{fmtTime(date)}</span>
                      </button>
                    ))}
                    {extra > 0 && (
                      <div className="px-1.5 text-[10px] font-medium text-gray-500 dark:text-gray-400">
                        {t('blogSeo.calendar.morePosts', '+{{count}} mais', { count: extra })}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Empty state for the visible month */}
      {!loading && !monthHasPosts && (
        <div className="flex flex-col items-center justify-center gap-2 py-8 text-center text-gray-500 dark:text-gray-400">
          <FileText className="w-6 h-6 opacity-60" />
          <span className="text-sm">{t('blogSeo.calendar.empty', 'Nenhum post agendado ou publicado neste mês.')}</span>
        </div>
      )}

      {/* Shared create/edit editor — same modal used in the Posts tab */}
      <BlogPostEditor
        isOpen={editorOpen}
        onClose={() => setEditorOpen(false)}
        post={editingPost}
        connections={connections}
        onSaved={() => {
          setEditorOpen(false);
          reloadPosts();
        }}
      />
    </div>
  );
};

export default BlogCalendar;
